import React, { useRef, useState } from 'react'
import { useApiListAuthSignInMethods } from '../../../../api-new/state/auth'
import { useLoad } from '../../../../shared/hooks/load'
import { IconSVG } from '../../../../shared/sso-workspace/provider-logo'
import { AuthNotice } from '../../../../shared/sso-workspace/auth-card'

// The sign-in methods offered to everyone on /auth/email, under "or": one
// button per platform sign-in method auth lists (GET /provider-link with no
// email), the ones staff switch on on the Sign-in methods page -- "Sign in
// with Microsoft" (the Microsoft Entra app gallery's required entry point,
// G1/G2), Google, Apple, Facebook -- in staff's order. The email step is
// untouched: nobody has to use one.
//
// The page asks with ?include=unavailable (design
// edxr-workspace/.superpowers/signin-methods §3): a method staff made
// "Temporarily unavailable" comes back with available: false and its notice,
// and is drawn as a disabled button with the notice under it, so the button
// stays where people expect it during an incident (the gallery listing
// needs it visible). Only clients that can draw that ask; installed apps
// get today's list, without them.
//
// A method starts the same SSO sign-in a connection does (hooks/provider.js,
// with no email); for Microsoft the server then routes a work account to its
// organization's own connection when it has one.
//
// The mark is auth's (iconSVG from the provider icon library; iconSVGDark
// for a single-colour mark in dark mode), drawn small beside the words, as
// Microsoft's branding guidelines draw theirs.

export const INCLUDE_UNAVAILABLE = '?include=unavailable'

// Whether a method can be used now: auth marks a temporarily unavailable
// one available: false (an answer without the field is today's: usable).
export const isAvailable = method => !method || method.available !== false

// usePublicSignInMethods asks once per page; a failed or empty answer offers
// none (the page is the same as before).
export const usePublicSignInMethods = () => {
  const api = useApiListAuthSignInMethods()
  const apiRef = useRef(api)
  apiRef.current = api
  const [methods, updateMethods] = useState([])

  useLoad(async () => {
    try {
      const answer = await apiRef.current.get(INCLUDE_UNAVAILABLE)
      const offered = Array.isArray(answer) ? answer.filter(m => m && m.identifier && m.displayName) : []
      if (offered.length) {
        updateMethods(offered)
      }
    } catch {}
  }, [])

  return methods
}

const MethodMark = ({ iconSVG, iconSVGDark }) =>
  iconSVG
    ? <span className={`sa-method-mark${iconSVGDark ? ' sw-logo-has-dark' : ''}`} aria-hidden="true" data-testid="sign-in-method-mark">
        <IconSVG svg={iconSVG} className="sw-logo-svg sw-logo-light" />
        {iconSVGDark && <IconSVG svg={iconSVGDark} className="sw-logo-svg sw-logo-dark" />}
      </span>
    : null

// What an unavailable method says under its button when staff left no
// notice.
export const UNAVAILABLE_TEXT = 'Temporarily unavailable.'

// SignInMethods: the buttons. starting is the method whose sign-in is
// starting (its button says so, every button waits); failed, one that
// could not start (the passive token request failed). A method that is
// temporarily unavailable is a disabled button with its notice under it
// (the button's description), and is never chosen.
export const SignInMethods = ({ methods, starting, failed, disabled, onChoose }) => {
  if (!methods || !methods.length) {
    return null
  }

  return (
    <div className="sa-methods">
      {failed &&
        <AuthNotice tone="bad" role="alert">
          <p className="m-0">We couldn't start signing you in with {failed.displayName}. Check your connection and try again.</p>
        </AuthNotice>}
      {methods.map(method => {
        const available = isAvailable(method)
        const noticeID = `sign-in-method-${method.identifier}-notice`
        const button =
          <button
            key={method.identifier}
            type="button"
            id={`sign-in-method-${method.identifier}`}
            className="btn sw-btn sa-block sa-method"
            disabled={!available || disabled || !!starting}
            aria-describedby={available ? undefined : noticeID}
            onClick={() => { if (available) onChoose(method) }}>
            <MethodMark iconSVG={method.iconSVG} iconSVGDark={method.iconSVGDark} />
            {starting && starting.identifier === method.identifier ? `Taking you to ${method.displayName}…` : `Sign in with ${method.displayName}`}
          </button>
        if (available) {
          return button
        }
        return (
          <div key={method.identifier} className="sa-method-item" data-testid="sign-in-method-unavailable">
            {button}
            <p className="sa-method-notice" id={noticeID}>{method.notice || UNAVAILABLE_TEXT}</p>
          </div>
        )
      })}
    </div>
  )
}
